/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/1/22, 3:54 PM
 *
 */

import React, { useState } from 'react';
import LetteredAvatar from 'react-avatar';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useAppDispatch } from 'stores';
import { ApiResponse } from 'types/api';
import { Member } from 'types/MemberTypes';

import { addUser } from 'features/auth';

import { privateAgent } from 'config/axios';

import { alert } from 'components/Alert';
import Button from 'components/Button';
import { Modal } from 'components/Modal/useModal';

interface IProfileImageModalProps {
  children: React.ReactNode;
  selectedMember: Member;
}

const ProfileImageModal = ({ children, selectedMember }: IProfileImageModalProps) => {
  const fileInput = React.createRef<any>();
  const { handleSubmit } = useForm();
  const dispatch = useAppDispatch();

  const [isNewImage, setIsNewImage] = useState(false);

  return (
    <Modal>
      <Modal.Button type="open">{children}</Modal.Button>
      <Modal.Content onClose={() => setIsNewImage(false)}>
        <div className="flex flex-col justify-center space-y-10">
          <div className="space-y-4 ">
            <h1 className="text-3xl font-medium text-gray-700">Change Photo</h1>
            <hr />
          </div>
          <div className="flex flex-col items-center space-y-8 self-center">
            <h1 className="text-2xl font-medium text-gray-800">
              {selectedMember.name.split(' ')[0]}, Keep your profile fresh
            </h1>
            <div
              className={`relative h-40 w-40 overflow-hidden rounded-full object-contain ${
                !isNewImage ? 'hidden' : 'block'
              }`}
            >
              <img id="output" className="rounded-full" alt="profile" />
            </div>

            {!isNewImage &&
              (selectedMember.image ? (
                <img
                  src={selectedMember.image}
                  className=" h-40 w-40 overflow-hidden rounded-full object-contain"
                />
              ) : (
                <div>
                  <LetteredAvatar
                    name={selectedMember.name}
                    size="120"
                    round={true}
                    maxInitials={2}
                  />
                </div>
              ))}

            <h1 className="text-center text-xl font-medium text-gray-700">
              Take a photo or upload a photo that really represents you. <br />
              File size must be less than 1Mb.
            </h1>
          </div>
          <div className="flex flex-col space-y-4">
            <hr />
            <Modal.Form
              className="flex items-center space-x-4 self-end "
              encType="multipart/form-data"
              onSubmit={handleSubmit(async () => {
                const fd = new FormData();
                fd.append('image', fileInput.current.files[0], fileInput.current.files[0].name);
                await alert({
                  type: 'promise',
                  promise: privateAgent
                    .post<ApiResponse<string>>('member/avatar', fd)
                    .then((response) => {
                      dispatch(
                        addUser({
                          ...selectedMember,
                          image: response.data.data as string,
                        })
                      );
                      setIsNewImage(false);
                    }),
                  msgs: {
                    loading: 'Uploading Profile',
                    success: 'Upload Successfully',
                    error: 'Upload Failed',
                  },
                  id: 'image-upload',
                });
              })}
            >
              <div className="flex space-x-2">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInput}
                  onChange={(e) => {
                    const reader = new FileReader();
                    reader.onload = function () {
                      const output = document.getElementById('output') as HTMLImageElement;
                      setIsNewImage(true);
                      if (typeof reader.result === 'string') {
                        output.src = reader.result;
                      }
                    };

                    if (e.target.files) {
                      if (e.target.files[0].size > 1 * 1024 * 1024) {
                        toast.error('File size must be less than 1MB');
                        return;
                      }
                      reader.readAsDataURL(e.target.files[0]);
                    }
                  }}
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="custom-file-upload">
                  Choose image
                </label>
              </div>

              <Button>Upload Photo</Button>
            </Modal.Form>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default ProfileImageModal;
